import React, { useState, useEffect, useRef } from 'react';
import {
  Code, Download, Copy, Check, X, Maximize2, Minimize2,
  FileText, Table, BarChart, Layout
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import '../styles/vault-modals.css';
import './Canvas.css';

const EnhancedCanvas = ({ message, onClose }) => {
  const [activeTab, setActiveTab] = useState('preview');
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('text');
  const [language, setLanguage] = useState('html');
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Detect content type and extract
    const detected = detectContentType(message);
    if (detected) {
      setContent(detected.content);
      setContentType(detected.type);
      setLanguage(detected.language || 'text');
    }
  }, [message]);

  // Detect what type of content this is
  const detectContentType = (msg) => {
    if (!msg || !msg.content) return null;

    const text = msg.content;

    // 1. CODE BLOCKS (highest priority)
    const codeBlockMatch = text.match(/```(\w+)?\n([\s\S]*?)```/);
    if (codeBlockMatch) {
      const lang = codeBlockMatch[1] || 'text';
      const code = codeBlockMatch[2].trim();
      return {
        type: 'code',
        content: code,
        language: lang.toLowerCase(),
        raw: text
      };
    }

    // 2. HTML CONTENT
    if (text.includes('<!DOCTYPE') || text.includes('<html>')) {
      return { type: 'code', content: text, language: 'html' };
    }

    // 3. TABLES (markdown tables)
    if (text.includes('|') && text.split('\n').filter(line => line.includes('|')).length >= 2) {
      return { type: 'table', content: text, language: 'markdown' };
    }

    // 4. STRUCTURED DOCUMENTS (headings, sections, lists)
    const hasMultipleHeadings = (text.match(/^#{1,3}\s/gm) || []).length >= 3;
    const hasLists = (text.match(/^[-*]\s/gm) || []).length >= 5;
    const wordCount = text.split(/\s+/).length;

    if ((hasMultipleHeadings || hasLists) && wordCount > 200) {
      return { type: 'document', content: text, language: 'markdown' };
    }

    // 5. LONG TEXT (reports, summaries, analyses)
    if (wordCount > 300) {
      return { type: 'document', content: text, language: 'markdown' };
    }

    return null;
  };

  // Get icon based on content type
  const getContentIcon = () => {
    switch (contentType) {
      case 'code': return Code;
      case 'document': return FileText;
      case 'table': return Table;
      case 'chart': return BarChart;
      default: return Layout;
    }
  };

  // Get tabs based on content type
  const getTabs = () => {
    if (contentType === 'code') {
      return ['preview', 'code'];
    }
    if (contentType === 'document') {
      return ['formatted', 'raw'];
    }
    if (contentType === 'table') {
      return ['table', 'markdown'];
    }
    return ['view'];
  };

  // Render content based on type and tab
  const renderContent = () => {
    // CODE PREVIEW
    if (contentType === 'code') {
      if (activeTab === 'preview') {
        return renderCodePreview();
      } else {
        return (
          <div className="vc-code-wrap">
            <CodeEditor code={content} language={language} onChange={setContent} />
          </div>
        );
      }
    }

    // DOCUMENT VIEW
    if (contentType === 'document') {
      if (activeTab === 'formatted') {
        return (
          <div className="vc-doc-wrap">
            <div className="vc-doc">
              <div className="vc-prose">
                <ReactMarkdown>{content}</ReactMarkdown>
              </div>
            </div>
          </div>
        );
      } else {
        return (
          <div className="vc-code-wrap">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="vc-textarea"
              spellCheck={false}
            />
          </div>
        );
      }
    }

    // TABLE VIEW
    if (contentType === 'table') {
      if (activeTab === 'table') {
        return (
          <div className="vc-doc-wrap">
            <div className="vc-prose">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          </div>
        );
      } else {
        return (
          <div className="vc-code-wrap">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="vc-textarea"
              spellCheck={false}
            />
          </div>
        );
      }
    }

    // DEFAULT VIEW
    return (
      <div className="vc-doc-wrap">
        <div className="vc-doc">
          <div className="vc-prose">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        </div>
      </div>
    );
  };

  // Render code preview
  const renderCodePreview = () => {
    if (language === 'html') {
      return (
        <iframe
          ref={iframeRef}
          srcDoc={content}
          className="vc-iframe"
          sandbox="allow-scripts allow-same-origin"
          title="HTML Preview"
        />
      );
    }

    if (language === 'jsx' || language === 'react') {
      return (
        <div className="vc-placeholder">
          <Code className="vc-placeholder-icon" />
          <p>React preview coming soon</p>
          <p className="vc-placeholder-sub">Switch to Code tab to view and edit</p>
        </div>
      );
    }

    if (language === 'svg') {
      return (
        <div
          className="vc-svg-stage"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    // For other code languages, show formatted code
    return (
      <div className="vc-code-view">
        <pre>
          <code>{content}</code>
        </pre>
      </div>
    );
  };

  // Copy content to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download file
  const handleDownload = () => {
    const extensions = {
      code: {
        html: 'html', jsx: 'jsx', react: 'jsx', javascript: 'js',
        typescript: 'ts', python: 'py', css: 'css', json: 'json'
      },
      document: 'md',
      table: 'md',
      text: 'txt'
    };

    let ext = 'txt';
    if (contentType === 'code' && language) {
      ext = extensions.code[language] || 'txt';
    } else {
      ext = extensions[contentType] || 'txt';
    }

    const filename = `canvas-${contentType}-${Date.now()}.${ext}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!content) {
    return (
      <div className="vault-canvas vc-empty">
        <div className="vc-empty-inner">
          <Layout className="vc-empty-icon" />
          <p>Canvas will appear here</p>
          <p className="vc-empty-sub">Ask for code, documents, or detailed content</p>
        </div>
      </div>
    );
  }

  const ContentIcon = getContentIcon();
  const tabs = getTabs();
  const tabLabels = {
    preview: 'Preview',
    code: 'Code',
    formatted: 'Formatted',
    raw: 'Raw',
    table: 'Table',
    markdown: 'Markdown',
    view: 'View'
  };

  return (
    <div className={`vault-canvas ${isFullscreen ? 'is-fullscreen' : ''}`}>
      {/* Header */}
      <div className="vc-head">
        <div className="vc-head-left">
          <ContentIcon className="vc-head-icon" />
          <h3 className="vc-title">Canvas</h3>
          <span className="vault-badge">{contentType}</span>
          {language && contentType === 'code' && (
            <span className="vault-badge vault-badge--free">{language}</span>
          )}
        </div>

        <div className="vc-head-right">
          {/* Tab Switcher */}
          {tabs.length > 1 && (
            <div className="vault-tabs vc-tabs">
              {tabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`vault-tab ${activeTab === tab ? 'is-active' : ''}`}
                >
                  {tabLabels[tab]}
                </button>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="vc-actions">
            <button
              onClick={handleCopy}
              className="vault-icon-btn"
              title="Copy content"
            >
              {copied ? <Check style={{ color: 'var(--accent)' }} /> : <Copy />}
            </button>
            <button
              onClick={handleDownload}
              className="vault-icon-btn"
              title="Download file"
            >
              <Download />
            </button>
            <button
              onClick={toggleFullscreen}
              className="vault-icon-btn"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 /> : <Maximize2 />}
            </button>
            {!isFullscreen && (
              <button
                onClick={onClose}
                className="vault-icon-btn"
                title="Close canvas"
              >
                <X />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="vc-content">
        {renderContent()}
      </div>
    </div>
  );
};

// Code editor component
const CodeEditor = ({ code, language, onChange }) => {
  return (
    <textarea
      value={code}
      onChange={(e) => onChange(e.target.value)}
      className="vc-textarea"
      spellCheck={false}
      style={{ tabSize: 2 }}
    />
  );
};

export default EnhancedCanvas;
